export const IMPERSONATION_KEY = 'devbar-impersonation-context';

export type ImpersonationValue = string | number | boolean | null;

export type ImpersonationContext = Record<string, ImpersonationValue>;

export const EMPTY_IMPERSONATION: ImpersonationContext = Object.freeze({});

const isImpersonationValue = (value: unknown): value is ImpersonationValue =>
  value === null ||
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean';

export const parseImpersonationContext = (
  raw: string,
): ImpersonationContext => {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return EMPTY_IMPERSONATION;
    }
    return Object.fromEntries(
      Object.entries(parsed).filter(([, value]) => isImpersonationValue(value)),
    ) as ImpersonationContext;
  } catch {
    return EMPTY_IMPERSONATION;
  }
};

export const applyImpersonation = <T extends object>(
  real: T,
  impersonated: ImpersonationContext,
  everImpersonatedKeys: Iterable<string>,
): T => {
  const overrides: Record<string, string | number | boolean | undefined> = {};

  for (const key of everImpersonatedKeys) {
    if (!(key in real) && !(key in impersonated)) {
      overrides[key] = undefined;
    }
  }

  for (const [key, value] of Object.entries(impersonated)) {
    overrides[key] = value === null ? undefined : value;
  }

  return { ...real, ...overrides };
};
